import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import BackIcon from "../../components/back-icon";
import { Input } from "../../components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../components/ui/form";
import { useForm } from "react-hook-form";
import {
  addHealthRecordSchema,
  AddHealthRecordSchema,
} from "../../lib/validation/add-health-record";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import { X } from "lucide-react";
import { useAppointmentData } from "../../contexts/appointment-data-context";

function AddHealthRecords() {
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirectUrl") || "/";
  const navigate = useNavigate();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data, setData } = useAppointmentData();

  const form = useForm<AddHealthRecordSchema>({
    resolver: zodResolver(addHealthRecordSchema),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newFiles = Array.from(e.target.files || []);
    
    // Check if there's already a file selected
    if (selectedFiles.length > 0) {
      setErrorMessage("Please upload 1 record with multiple pages at a time");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Allow only one file
    if (newFiles.length > 1) {
      setErrorMessage("Please upload 1 record with multiple pages at a time");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // If a single file is selected, clear any error and set the file
    if (newFiles.length === 1) {
      setSelectedFiles(newFiles);
      setErrorMessage(null);
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleRemoveFile(fileToRemove: File) {
    setSelectedFiles((prevFiles) =>
      (prevFiles ?? []).filter(
        (file) =>
          !(file.name === fileToRemove.name && file.size === fileToRemove.size),
      ),
    );
    setErrorMessage(null);
    // Ensure file input is fully reset to allow new uploads
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function handleAddRecord({ physician }: AddHealthRecordSchema) {
    setData({
      ...data,
      healthRecordPhysician: physician,
      healthRecords: selectedFiles,
      branchDetails: data?.branchDetails ?? [],
    });
    navigate(redirectUrl);
  }

  // Truncate file name for display (max 20 chars for mobile)
  function truncateFileName(fileName: string, maxLength: number = 20): string {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    return `${nameWithoutExt.substring(0, maxLength - 3 - (extension?.length || 0))}...${extension}`;
  }

  return (
    <div className="container flex min-h-screen max-w-md flex-col py-4 sm:justify-center">
      <div className="mb-4 flex items-center gap-2 sm:mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="relative group">
          <BackIcon />
          <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:flex items-center p-1 rounded text-xs text-white bg-gray-800 shadow-lg z-50">
            Back
          </span>
        </Button>
        <h1 className="text-sm font-bold">Add Record</h1>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleAddRecord)}
          className="space-y-8"
        >
          <div>
            <label
              htmlFor="health-record-file"
              className="inline-flex h-12 w-full cursor-pointer items-center justify-between rounded-md border border-input px-3 py-2 text-xs"
            >
              <span>Upload Record</span>
              <span className="font-bold text-primary">Select</span>
            </label>
            <input
              type="file"
              ref={fileInputRef}
              className="sr-only"
              id="health-record-file"
              multiple
              accept=".pdf,.jpeg,.jpg,.png"
              onChange={handleFileChange}
            />
            <ul className="mt-1 flex flex-wrap gap-2">
              {selectedFiles?.map((file) => (
                <li
                  key={file.name}
                  className="flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-sm"
                >
                  <p>{truncateFileName(file.name)}</p>
                  <button
                    type="button"
                    className="p-1"
                    onClick={() => handleRemoveFile(file)}
                  >
                    <X className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
            {!errorMessage && (
              <p className="mt-2 text-[11px] text-muted-foreground sm:text-sm">
                Please upload 1 record with multiple pages at a time
              </p>
            )}
            {errorMessage && (
              <p className="mt-2 text-[11px] text-red-500 sm:text-sm">
                {errorMessage}
              </p>
            )}
          </div>

          <FormField
            control={form.control}
            name="physician"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input placeholder="Doctor Name or Lab Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="bottom-5 left-0 w-full space-y-4 max-sm:fixed max-sm:px-4">
            <Button
              type="submit"
              className="w-full font-bold shadow-xl"
              size="lg"
            >
              Upload
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default AddHealthRecords;